// @ts-nocheck
import { environment } from '../../../environments/environment';

const CODE_API = {
  OK_RESTRICTED: 200,
  OK_UNRESTRICTED: 201,
  ERROR_DENY: 400,
  NOT_PERMITTED: 403,
  ERROR_DB: 500,
  ERROR_PARAM_NOT_FOUND: 501,
  SQL_ERROR: 1064,
} as const;

type QueryParam = Record<string, string | number>;

type QueryParams<T extends QueryParam> = {
  [K in keyof T]: T[K];
};

// Note: POST_V1 is just used to perform a post in APIV1Adapter
type RequestArgs<T, U> = {
  method: 'GET' | 'POST' | 'POST_V1' | 'PUT' | 'DELETE';
  body?: T;
  queryParams?: U;
  params?: { uid: number };
};

type ResponseCode = (typeof CODE_API)[keyof typeof CODE_API];

/**
 * Service which generically manage operations to REST API.
 */
class ApiService<T, U extends QueryParam = {}> {
  protected baseUrl = `${environment.apiBaseUrl}${environment.apiVersion}`;

  protected token = localStorage.getItem('token'); // TODO: replace by token from Authentication

  protected entity!: string;

  constructor(entity: string) {
    this.entity = entity;
  }

  static toQueryString(queryParams: Record<string, any>) {
    return new URLSearchParams(queryParams).toString();
  }

  public async request(args: RequestArgs<T, QueryParams<U>>) {
    const queryString = args.queryParams ? ApiService.toQueryString(args.queryParams) : '';
    const url = `${this.baseUrl}/${args.params ? args.params.id : ''}${this.entity}?${queryString}`;

    const formattedBody = { ...(args.body || {}), token: this.token };

    try {
      const response = await fetch(url, {
        method: args.method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formattedBody),
      });

      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }
      return response.json();
    } catch (err) {
      console.error('Error occurred in API service: ', new ApiError(err), err);
    }
  }

  public async get(args: { queryParams?: QueryParams<U> } = {}): Promise<T[]> {
    return this.request({ method: 'GET', queryParams: args.queryParams });
  }

  public async getById(args: { queryParams?: QueryParams<U>; id: number }): Promise<T> {
    return this.request({ method: 'GET', params: { uid: args.id }, queryParams: args.queryParams });
  }

  public async post(body: T): Promise<T> {
    return this.request({ method: 'POST', body });
  }

  public async put(body: T): Promise<T> {
    return this.request({ method: 'PUT', body });
  }

  // TODO: Change params
  public async delete(uid: number): Promise<void> {
    await this.request({ method: 'DELETE', params: { uid } });
  }
}

export default ApiService;
export { CODE_API };
export type { QueryParam, QueryParams, RequestArgs, ResponseCode };
